/**
 *  @class
 *  @function ModalDialog
 */
if (!customElements.get('modal-dialog')) {
  class ModalDialog extends HTMLElement {
    constructor() {
      super();

      this.age_verification = this.classList.contains('age-verification-modal');
      this.id = this.getAttribute('id');
      this.delay = parseInt(this.dataset.delay, 10) * 1000;
      this.popup = this.dataset.popup;
      this.section_id = this.dataset.sectionId;
      this.disabled = this.getAttribute('disabled') != undefined;
      this.button = this.querySelector('[id^="ModalClose-"]');

      this.button.addEventListener('click', this.hide.bind(this));

      // Reuse the native popup on the immersive shell without allowing focus
      // to escape into the feed, sticky CTA or chat behind its modal backdrop.
      if (this.popup) {
        this.dialog = this.querySelector('[role="dialog"]');
        const heading = this.querySelector('.product-popup-modal--heading');
        if (heading && !this.dialog.hasAttribute('aria-labelledby')) {
          heading.id = heading.id || `${this.id}-heading`;
          this.dialog.setAttribute('aria-labelledby', heading.id);
        }
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
          const controls = Array.from(this.dialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
          )).filter((element) => element.getClientRects().length);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (!first) {
            event.preventDefault();
            this.dialog.focus();
          } else if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === this.dialog)) {
            event.preventDefault();
            first.focus();
          }
        });
        document.addEventListener('focusin', (event) => {
          // Shopify's native form protection opens a separate cross-origin
          // challenge above this dialog. Allow its own focus management;
          // never intercept or modify the challenge itself.
          const platformChallenge = event.target.matches?.(
            'iframe[src^="https://newassets.hcaptcha.com/"][title="hCaptcha challenge"]'
          );
          if (this.hasAttribute('open') && !this.contains(event.target) && !platformChallenge) this.dialog.focus();
        });
      }

      if (!this.disabled) {
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') {
            this.hide();
          }
        });
        this.addEventListener('click', (event) => {
          if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
        });
      }
      if (this.dataset.leadPreview === 'true') {
        this.scheduleLeadPreview();
      } else if (this.delay && this.delay > 0 && !this.getCookie()) {
        setTimeout(() => {
          if (!this.getCookie() && !this.hasAttribute('open')) this.show();
        }, this.delay);
      }
      if (this.age_verification) {
        if (!this.getCookie()) {
          this.show();
        }
        this.button.addEventListener('click', this.setCookie.bind(this));
      }
      if (Shopify.designMode) {
        this.moved = true;
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.section_id) {
            this.show();
          }
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.section_id) {
            this.hide();
          }
        });
      }
    }

    scheduleLeadPreview() {
      if (this.leadSuppressed()) return;
      const engagedAt = Date.now() + 3000;
      const openPopup = () => {
        if (!this.leadSuppressed() && !this.hasAttribute('open')) this.show();
      };
      setTimeout(openPopup, this.delay);
      // Capture also sees MCM's nested feed scroll events.
      const onScroll = (event) => {
        if (this.contains(event.target)) return;
        const scroller = event.target === document ? document.scrollingElement : event.target;
        const distance = scroller.scrollHeight - scroller.clientHeight;
        if (distance <= 0 || scroller.scrollTop / distance < 0.4) return;
        document.removeEventListener('scroll', onScroll, true);
        setTimeout(openPopup, Math.max(0, engagedAt - Date.now()));
      };
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    }

    leadSuppressed() {
      try {
        return this.getCookie() || Boolean(window.sessionStorage.getItem('velven_lead_preview_visit')) ||
          /(?:^|;\s*)(?:velven_lead_preview_visit|velven_lead_preview_signed_up|velven_lead_signed_up|velven_has_purchased)=1(?:;|$)/.test(document.cookie);
      } catch (_) {
        return true;
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener, resumeLeadPreview = false) {
      if (this.dataset.leadPreview === 'true') {
        // A restored signup must finish even though its first display marked this visit.
        if (!resumeLeadPreview && this.leadSuppressed()) return;
        try {
          window.sessionStorage.setItem('velven_lead_preview_visit', '1');
          document.cookie = 'velven_lead_preview_visit=1; Path=/; SameSite=Lax; Secure';
        } catch (_) { return; }
      }
      this.openedBy = opener || document.activeElement;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');

      setTimeout(() => {
        if (this.hasAttribute('open')) this.querySelector('[role="dialog"]').focus();
      }, 100);
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      this.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
      });
      this.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      this.querySelectorAll('video').forEach((video) => video.pause());

      if (this.popup) {
        this.setCookie();
        if (this.openedBy && this.openedBy.isConnected) this.openedBy.focus();
      }
    }

    getCookie() {
      if (this.dataset.leadPreview !== 'true') return window.localStorage.getItem(this.id);
      try {
        const raw = window.localStorage.getItem('velven_lead_preview_dismissed_at') || window.localStorage.getItem(this.id);
        if (!raw) return false;
        const value = JSON.parse(raw);
        const dismissedAt = typeof value === 'number' ? value : Date.parse(value);
        return Number.isFinite(dismissedAt) && Date.now() < dismissedAt + 14 * 86400000;
      } catch (_) {
        return true;
      }
    }
    setCookie() {
      if (this.dataset.leadPreview === 'true') {
        try { window.localStorage.setItem('velven_lead_preview_dismissed_at', JSON.stringify(Date.now())); } catch (_) { /* No tracking fallback. */ }
      } else {
        window.localStorage.setItem(this.id, JSON.stringify(new Date()));
      }
    }
  }
  customElements.define('modal-dialog', ModalDialog);
}
if (!customElements.get('modal-opener')) {
  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector('button');

      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
  }
  customElements.define('modal-opener', ModalOpener);
}